import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Instagram, Facebook, Linkedin, CheckCircle } from "lucide-react";

const ContactFillForm = () => {
  const [status, setStatus] = useState({
    submitting: false,
    submitted: false,
    error: null,
  });

  // react-hook-form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data) => {
    setStatus({ submitting: true, submitted: false, error: null });

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: "YOUR_ACCESS_KEY_HERE", // Web3Forms key yahan dalein
          from_name: `${data.firstName} ${data.lastName}`,
          ...data,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setStatus({ submitting: false, submitted: true, error: null });
        reset(); // Form clear karne ke liye
      } else {
        throw new Error(result.message || "Something went wrong");
      }
    } catch (err) {
      setStatus({
        submitting: false,
        submitted: false,
        error: "Failed to send message. Please try again.",
      });
    }
  };

  // Reusable error message component
  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <p className="text-red-500 text-sm mt-1">{errors[name].message}</p>
    ) : null;

  return (
    <div className="max-w-[1800px] mx-auto p-4 md:p-12 bg-white font-sans">
      <div className="flex flex-col lg:flex-row gap-12">
        {/* Left Side: Contact Form */}
        <div className="flex-1">
          <h2 className="text-[24px] md:text-[30px] lg:text-[40px] 2xl:text-[60px] font-bold text-[#183076] mb-2">
            Contact Us
          </h2>
          <p className="text-[18px] xl:text-[24px] mb-8">
            Join us in Creating Something Great
          </p>

          {status.submitted ? (
            <div className="bg-green-50 border border-green-200 p-8 rounded-2xl flex flex-col items-center text-center gap-4">
              <CheckCircle className="text-green-500 w-16 h-16" />
              <h3 className="text-2xl font-bold text-green-800">
                Message Sent!
              </h3>
              <p className="text-green-700">
                Thank you for reaching out to Avinya Research Lab.
              </p>
              <button
                onClick={() => setStatus({ ...status, submitted: false })}
                className="mt-4 text-[#183076] font-bold hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register("firstName", {
                      required: "First name is required",
                    })}
                    placeholder="First Name"
                    className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 border-[#183076] text-[18px] xl:text-[24px] placeholder-[#183076] ${errors.firstName ? "border-red-500" : ""}`}
                  />
                  <ErrorMsg name="firstName" />
                </div>
                <div>
                  <input
                    {...register("lastName", {
                      required: "Last name is required",
                    })}
                    placeholder="Last Name"
                    className="w-full p-3 border border-[#183076] rounded-lg text-[18px] xl:text-[24px] placeholder-[#183076]"
                  />
                  <ErrorMsg name="lastName" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                    placeholder="Email"
                    className="w-full p-3 border border-[#183076] rounded-lg text-[18px] xl:text-[24px] placeholder-[#183076]"
                  />
                  <ErrorMsg name="email" />
                </div>

                {/* Phone Input Integration */}
                <div>
                  <Controller
                    name="phone"
                    control={control}
                    rules={{ required: "Phone number is required" }}
                    render={({ field }) => (
                      <PhoneInput
                        {...field}
                        country={"in"}
                        inputStyle={{
                          width: "100%",
                          height: "54px",
                          fontSize: "18px",
                          borderColor: errors.phone ? "#ef4444" : "#183076",
                          borderRadius: "8px",
                        }}
                        containerClass="phone-container"
                      />
                    )}
                  />
                  <ErrorMsg name="phone" />
                </div>
              </div>

              <div>
                <input
                  {...register("subject", { required: "Subject is required" })}
                  placeholder="Subject"
                  className="w-full p-3 border border-[#183076] rounded-lg text-[18px] xl:text-[24px] placeholder-[#183076]"
                />
                <ErrorMsg name="subject" />
              </div>

              <div>
                <textarea
                  {...register("message", {
                    required: "Message cannot be empty",
                  })}
                  placeholder="Message"
                  rows="6"
                  className="w-full p-3 border border-[#183076] rounded-lg text-[18px] xl:text-[24px] placeholder-[#183076]"
                ></textarea>
                <ErrorMsg name="message" />
              </div>

              {status.error && (
                <p className="text-red-500 font-medium">{status.error}</p>
              )}

              <button
                type="submit"
                disabled={status.submitting}
                className={`flex items-center gap-2 bg-[#183076] text-white px-10 py-4 rounded-full font-bold border border-[#183076] transition-all text-[16px] md:text-[20px] 2xl:text-[22px] ${
                  status.submitting
                    ? "opacity-70 cursor-not-allowed"
                    : "hover:bg-white hover:text-[#183076]"
                }`}
              >
                {status.submitting ? "Sending..." : "Submit"}
              </button>
            </form>
          )}
        </div>

        {/* Right Side Card (Address/Contact) - remains same as your original */}
        <div className="lg:w-1/3 bg-[#183076] text-white p-8 md:p-10 rounded-[2rem] shadow-xl">
          {/* Same contact info content as before */}
          <div className="space-y-8">
            <div>
              <h3 className="text-[20px] md:text-[24px] 2xl:text-[30px] font-bold mb-3">
                Address
              </h3>
              <p className="text-[18px] xl:text-[24px]">
                4th Floor, C/o Priya Hospital,
              </p>
              <p className="text-[18px] xl:text-[24px]">
                Anand – 388 001, Gujarat, India
              </p>
            </div>
            <div>
              <h3 className="text-[20px] md:text-[24px] 2xl:text-[30px] font-bold mb-3">
                Contact
              </h3>
              <p className="text-[18px] xl:text-[24px]">+91 92747 92400</p>
            </div>
            <div>
              <h3 className="text-[20px] md:text-[24px] 2xl:text-[30px] font-bold mb-3">
                Email
              </h3>
              <p className="text-[18px] xl:text-[24px]">
                info@avinyaresearchlab.com
              </p>
              {/* <p className="text-[18px] xl:text-[24px]">
                support@avinyaresearchlab.com
              </p> */}
            </div>
            <div className=" gap-4 pt-4 ">
              <h3 className="text-[20px] md:text-[24px] 2xl:text-[30px] font-bold mb-3">
                Stay Connected
              </h3>
              {/* <div className="bg-white p-2 rounded-full text-[#183076]">
                <Instagram size={20} />
              </div>
              <div className="bg-white p-2 rounded-full text-[#183076]">
                <Facebook size={20} />
              </div> */}
              <div className="flex">
                <div className="bg-white p-2 rounded-full text-[#183076]">
                  <Linkedin size={20} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactFillForm;
